import { createContext, ReactNode, useCallback, useContext, useRef } from 'react';
import { EventSearchForm } from '@mark43/cad-api';
import { useSearchState } from '../fr-page-layout/search-pages/use-search-state';
import { EventSearchFormType } from './types';

// Keep this wrapper so ReturnType can derive the exact context value shape from useSearchState.
function useFrEventSearchStateValue() {
    const searchState = useSearchState<EventSearchFormType, EventSearchForm>();
    // Draft updates happen on every keystroke, so keep them out of React state renders.
    const draftFormValuesRef = useRef<EventSearchFormType>();
    const getDraftFormValues = useCallback(() => draftFormValuesRef.current, []);
    const setDraftFormValues = useCallback((draftFormValues: EventSearchFormType | undefined) => {
        draftFormValuesRef.current = draftFormValues;
    }, []);
    const clearDraftFormValues = useCallback(() => {
        draftFormValuesRef.current = undefined;
    }, []);

    return {
        ...searchState,
        getDraftFormValues,
        setDraftFormValues,
        clearDraftFormValues,
    };
}

type FrEventSearchStateContextValue = ReturnType<typeof useFrEventSearchStateValue>;

const FrEventSearchStateContext = createContext<FrEventSearchStateContextValue | undefined>(
    undefined
);

export function useFrEventSearchStateContext() {
    const context = useContext(FrEventSearchStateContext);
    if (context === undefined) {
        throw new Error(
            'useFrEventSearchStateContext must be used within a FrEventSearchStateProvider'
        );
    }
    return context;
}

export function FrEventSearchStateProvider({ children }: { children: ReactNode }) {
    const stateValue = useFrEventSearchStateValue();

    return (
        <FrEventSearchStateContext.Provider value={stateValue}>
            {children}
        </FrEventSearchStateContext.Provider>
    );
}
